import { useCallback, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { startOfDay } from 'date-fns';
import type { ActiveTimer, Task } from '@timato/shared';
import { notDeleted } from '@/data/repo';
import { db } from '@/db/db';
import { useCompletionStore } from '@/timer/completionStore';
import { requestFromSession, switchTrack } from '@/timer/startTimer';
import { switchTargets, type SwitchTarget } from '@/timer/switchTargets';

export type Switcher = { targets: SwitchTarget[]; tasks: Map<string, Task>; switchTo: (t: SwitchTarget) => Promise<void> };

/**
 * The parked timers (TW-30): today's parked runs in Track (TW-32), and the one-step move back to any of them. Read here
 * once and handed to both the list and the number keys, so a key always picks the row it is printed on.
 */
export function useSwitcher(timer: ActiveTimer, now: Date): Switcher {
  const from = startOfDay(now).toISOString();
  const to = new Date(startOfDay(now).getTime() + 86_400_000).toISOString();
  const today = useLiveQuery(async () => {
    const sessions = (await db.sessions.where('startedAt').between(from, to, true, false).toArray()).filter(notDeleted);
    const ids = [...new Set(sessions.flatMap((s) => (s.taskId ? [s.taskId] : [])))];
    const tasks = (await db.tasks.bulkGet(ids)).filter((t): t is Task => !!t && !t.deletedAt);
    return { sessions, tasks: new Map(tasks.map((t) => [t.id, t])) };
  }, [from, to]);
  const { taskId, projectId, goalText } = timer;
  const targets = useMemo(
    () => (timer.mode === 'track' && today ? switchTargets(today.sessions, { taskId, projectId, goalText }, today.tasks) : []),
    [timer.mode, today, taskId, projectId, goalText],
  );
  // Stable, so the number keys are not bound again on every tick of the clock.
  const switchTo = useCallback(async (t: SwitchTarget) => {
    // A switch is not an ending: the last session's card would only stand in the way of the next one.
    useCompletionStore.getState().set(null);
    await switchTrack(await requestFromSession(t.session));
  }, []);
  return { targets, tasks: today?.tasks ?? new Map(), switchTo };
}
